import React, { useState } from 'react';
import { ShoppingBag, Globe, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const toggleLanguage = () => {
    const newLang = i18n.language === 'pl' ? 'en' : 'pl';
    i18n.changeLanguage(newLang);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    if (!isHome) {
      window.location.href = `/#${id}`;
      return;
    }
    
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const scrollToFooter = () => {
    const footer = document.querySelector('footer');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const getNavItemClass = (path: string) => {
    if (path === 'seo' && location.pathname === '/seo') {
      return 'text-blue-300 font-medium';
    }
    return 'text-blue-200/80 hover:text-blue-300 transition-colors';
  };

  return (
    <nav className="fixed w-full z-50 bg-slate-950/80 backdrop-blur-lg border-b border-white/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link 
            to="/"
            onClick={scrollToTop}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity group"
            aria-label="Go to homepage"
          >
            <ShoppingBag className="w-8 h-8 text-blue-300" aria-hidden="true" />
            <span className="text-2xl font-bold text-gradient text-glow mr-4">Market Engine</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('services')} 
              className={getNavItemClass('services')}
            >
              {t('navigation.services')}
            </button>
            <button 
              onClick={() => scrollToSection('advantages')} 
              className={getNavItemClass('advantages')}
            >
              {t('navigation.advantages')}
            </button>
            <button 
              onClick={() => scrollToSection('portfolio')} 
              className={getNavItemClass('portfolio')}
            >
              {t('navigation.portfolio')}
            </button>
            <button 
              onClick={() => scrollToSection('pricing')} 
              className={getNavItemClass('pricing')}
            >
              {t('navigation.pricing')}
            </button>
            <Link 
              to="/seo"
              className={getNavItemClass('seo')}
            >
              SEO
            </Link>

            <div className="relative group">
              <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-blue-500/50 opacity-75 blur-sm group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 opacity-75 group-hover:opacity-100 transition-opacity"></div>
              <button
                onClick={toggleLanguage}
                className="relative px-4 py-2 bg-slate-950 rounded-lg flex items-center space-x-2 text-blue-300 transition-all group-hover:scale-[1.02]"
                aria-label={`Change language to ${i18n.language === 'pl' ? 'English' : 'Polski'}`}
              >
                <Globe className="w-5 h-5" aria-hidden="true" />
                <span>{i18n.language ? i18n.language.toUpperCase() : 'PL'}</span>
              </button>
            </div>

            <div className="relative group">
              <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-blue-500/50 opacity-75 blur-sm group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 opacity-75 group-hover:opacity-100 transition-opacity"></div>
              <button 
                onClick={scrollToFooter}
                className="relative px-6 py-2 bg-slate-950 rounded-lg text-blue-300 transition-all group-hover:scale-[1.02]"
              >
                {t('navigation.contact')}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-4 md:hidden">
            <div className="relative group">
              <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-blue-500/50 opacity-75 blur-sm group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 opacity-75 group-hover:opacity-100 transition-opacity"></div>
              <button
                onClick={toggleLanguage}
                className="relative px-3 py-1.5 bg-slate-950 rounded-lg flex items-center space-x-1 text-blue-300 transition-all group-hover:scale-[1.02]"
                aria-label={`Change language to ${i18n.language === 'pl' ? 'English' : 'Polski'}`}
              >
                <Globe className="w-4 h-4" aria-hidden="true" />
                <span className="text-sm">{i18n.language ? i18n.language.toUpperCase() : 'PL'}</span>
              </button>
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-blue-300 hover:text-blue-200 transition-colors"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div 
            className="md:hidden absolute left-0 right-0 top-16 bg-slate-950/95 backdrop-blur-lg border-b border-white/10"
            role="dialog"
            aria-modal="true"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              <button 
                onClick={() => scrollToSection('services')} 
                className={`${getNavItemClass('services')} text-left py-2`}
              >
                {t('navigation.services')}
              </button>
              <button 
                onClick={() => scrollToSection('advantages')} 
                className={`${getNavItemClass('advantages')} text-left py-2`}
              >
                {t('navigation.advantages')}
              </button>
              <button 
                onClick={() => scrollToSection('portfolio')} 
                className={`${getNavItemClass('portfolio')} text-left py-2`}
              >
                {t('navigation.portfolio')}
              </button>
              <button 
                onClick={() => scrollToSection('pricing')} 
                className={`${getNavItemClass('pricing')} text-left py-2`}
              >
                {t('navigation.pricing')}
              </button>
              <Link 
                to="/seo"
                className={`${getNavItemClass('seo')} py-2`}
                onClick={() => setIsMenuOpen(false)}
              >
                SEO
              </Link>
              <div className="relative group">
                <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-blue-500/50 opacity-75 blur-sm group-hover:opacity-100 transition-opacity"></div>
                <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 opacity-75 group-hover:opacity-100 transition-opacity"></div>
                <button 
                  onClick={scrollToFooter}
                  className="relative w-full px-4 py-2 bg-slate-950 rounded-lg text-blue-300 transition-all group-hover:scale-[1.02]"
                >
                  {t('navigation.contact')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};